import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { clsx } from 'clsx'
import { z } from 'zod'
import { isValidCpf } from '@/shared/lib/cpf'
import { useUpdatePixKey, type PersonalData, type PixKeyType } from '../queries'
import styles from './AccountForm.module.css'

const TYPES: { value: PixKeyType; label: string; icon: string }[] = [
  { value: 'CPF',   label: 'CPF',       icon: '🆔' },
  { value: 'EMAIL', label: 'E-mail',    icon: '✉️' },
  { value: 'PHONE', label: 'Telefone',  icon: '📱' },
  { value: 'EVP',   label: 'Aleatória', icon: '🎲' },
]

const PLACEHOLDER: Record<PixKeyType, string> = {
  CPF:   '000.000.000-00',
  EMAIL: 'voce@email.com',
  PHONE: '(11) 90000-0000',
  EVP:   'chave aleatória (32 caracteres)',
}

const schema = z.object({
  keyType:  z.enum(['CPF', 'EMAIL', 'PHONE', 'EVP']),
  keyValue: z.string().trim().min(1, 'Informe a chave Pix.'),
}).superRefine((val, ctx) => {
  const v = val.keyValue
  const ok =
    val.keyType === 'CPF'   ? isValidCpf(v)
    : val.keyType === 'EMAIL' ? /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)
    : val.keyType === 'PHONE' ? (() => { const n = v.replace(/\D/g, '').length; return n >= 10 && n <= 13 })()
    : v.replace(/[^a-zA-Z0-9]/g, '').length === 32
  if (!ok) ctx.addIssue({ code: 'custom', path: ['keyValue'], message: 'Chave Pix inválida.' })
})

type FormValues = z.infer<typeof schema>

export function PixKeyForm({ data }: { data: PersonalData }) {
  const mutation = useUpdatePixKey()
  const {
    register, handleSubmit, setValue, watch, setError, reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { keyType: data.pixKeyType ?? 'CPF', keyValue: '' },
  })

  const keyType = watch('keyType')

  function onSubmit(values: FormValues) {
    mutation.mutate(values, {
      onSuccess: () => reset({ keyType: values.keyType, keyValue: '' }),
      onError: () => setError('keyValue', { message: 'Não foi possível salvar a chave. Tente novamente.' }),
    })
  }

  return (
    <form className={clsx(styles.card, styles.cardMoney)} onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={styles.cardHead}>
        <span className={clsx(styles.cardTitle, styles.cardTitleMoney)}>Chave Pix para recebimento</span>
        <span className={styles.cardHint}>Para onde enviamos seus prêmios</span>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.field} style={{ marginBottom: 18 }}>
          <span className={styles.label}>Tipo da chave</span>
          <div className={styles.pixTypes}>
            {TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                className={clsx(styles.pixType, keyType === t.value && styles.pixTypeActive)}
                onClick={() => setValue('keyType', t.value, { shouldValidate: false })}
                aria-pressed={keyType === t.value}
              >
                <span aria-hidden="true">{t.icon}</span>{t.label}
              </button>
            ))}
          </div>
        </div>

        <div className={clsx(styles.field, styles.col2)}>
          <label className={styles.label} htmlFor="pixKeyValue">Chave Pix</label>
          <input
            id="pixKeyValue"
            className={clsx(styles.input, styles.mono, errors.keyValue && styles.hasError)}
            placeholder={data.hasPixKey ? (data.pixKeyMask ?? PLACEHOLDER[keyType]) : PLACEHOLDER[keyType]}
            {...register('keyValue')}
          />
          {errors.keyValue
            ? <span className={styles.error}>{errors.keyValue.message}</span>
            : <span className={styles.help}>
                A titularidade da chave é confirmada antes do primeiro saque. Pagamentos via Pix
                chegam em breve — você já pode deixar cadastrado.
              </span>}
        </div>

        <div className={styles.actions}>
          {mutation.isSuccess && <span className={styles.savedNote}>✓ Chave salva</span>}
          <button type="submit" className={clsx(styles.btnPrimary, styles.btnMoney)}
            disabled={isSubmitting || mutation.isPending}>
            {mutation.isPending ? 'Salvando…' : 'Salvar chave'}
          </button>
        </div>
      </div>
    </form>
  )
}
